import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, ExternalLink, Loader2, Pencil, Plus, QrCode, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  countryCounts,
  goPublicUrl,
  isPrintedGoSlug,
  loadGoLinksDashboard,
  type GoClick,
  type GoLink,
  type GoLinkStats,
} from '../../lib/goLinks';
import GoLinkModal from './GoLinkModal';
import GoQrModal from './GoQrModal';

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function GoPublicUrlPopover({
  slug,
  destinationUrl,
  onCreateQr,
}: {
  slug: string;
  destinationUrl: string;
  onCreateQr: () => void;
}) {
  const url = goPublicUrl(slug);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number>();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 22 * 16;
    const left = Math.min(rect.left, window.innerWidth - width - 12);
    setPos({ top: rect.bottom + 6, left: Math.max(12, left) });
  }, []);

  const show = () => {
    window.clearTimeout(hideTimer.current);
    place();
    setOpen(true);
  };

  const hide = () => {
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = () => place();
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${url}`);
    } catch {
      toast.error('Could not copy URL');
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={(e) => {
          if (!panelRef.current?.contains(e.relatedTarget as Node)) hide();
        }}
        onClick={() => (open ? setOpen(false) : show())}
        className="inline-flex items-center gap-1 font-medium text-emerald-700 dark:text-emerald-300 hover:underline"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        /{slug}
      </button>
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label={`Full go link for /${slug}`}
              data-go-url-pop
              onMouseEnter={show}
              onMouseLeave={hide}
              style={{ top: pos.top, left: pos.left }}
              className="fixed z-50 w-[min(22rem,calc(100vw-1.5rem))] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-3 shadow-lg"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Go link
              </p>
              <p className="mt-1 break-all font-mono text-xs text-gray-900 dark:text-gray-100">{url}</p>
              {destinationUrl ? (
                <>
                  <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Lands on
                  </p>
                  <p className="mt-1 break-all font-mono text-xs text-gray-600 dark:text-gray-300">
                    {destinationUrl}
                  </p>
                </>
              ) : null}
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void copyUrl()}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <Copy className="h-3 w-3" />
                  Copy
                </button>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onCreateQr();
                  }}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <QrCode className="h-3 w-3" />
                  QR
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function ClickBar({ clicks, max }: { clicks: number; max: number }) {
  const width = max > 0 ? Math.max(4, Math.round((clicks / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[8rem]">
      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
        <div
          className="h-full rounded-full bg-emerald-500"
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="tabular-nums text-xs text-gray-600 dark:text-gray-300 w-8 text-right">
        {clicks}
      </span>
    </div>
  );
}

export default function GoLinksView() {
  const [stats, setStats] = useState<GoLinkStats[]>([]);
  const [clicks, setClicks] = useState<GoClick[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<GoLink | null>(null);
  const [qrSlug, setQrSlug] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadGoLinksDashboard();
      setStats(data.stats);
      setClicks(data.clicks);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to load go links');
      setStats([]);
      setClicks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const totalClicks = clicks.length;
  const maxClicks = Math.max(0, ...stats.map((row) => row.clicks));
  const countries = useMemo(() => countryCounts(clicks).slice(0, 8), [clicks]);
  const recent = clicks.slice(0, 25);

  const copySlugUrl = async (slug: string) => {
    const url = goPublicUrl(slug);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${url}`);
    } catch {
      toast.error('Could not copy URL');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <QrCode className="h-7 w-7 text-emerald-500" />
            Go links
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Destinations live in this table. The Worker reads them — no JSON deploy to retarget a QR.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            title="New go link"
            aria-label="New go link"
            className="inline-flex items-center justify-center p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Clicks logged
          </p>
          <p className="mt-2 text-3xl font-semibold tabular-nums text-gray-900 dark:text-white">
            {totalClicks.toLocaleString()}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Slugs
          </p>
          <p className="mt-2 text-3xl font-semibold tabular-nums text-gray-900 dark:text-white">
            {stats.length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Last click
          </p>
          <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
            {formatWhen(clicks[0]?.created_at ?? null)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <section className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Click map</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2">Slug</th>
                    <th className="px-4 py-2">Lands on</th>
                    <th className="px-4 py-2">Clicks</th>
                    <th className="px-4 py-2">Last</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {stats.map((row) => (
                    <tr
                      key={row.slug}
                      className="border-t border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-200"
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1">
                          <GoPublicUrlPopover
                            slug={row.slug}
                            destinationUrl={row.destination_url}
                            onCreateQr={() => setQrSlug(row.slug)}
                          />
                          <button
                            type="button"
                            onClick={() => void copySlugUrl(row.slug)}
                            title={`Copy ${goPublicUrl(row.slug)}`}
                            aria-label={`Copy ${goPublicUrl(row.slug)}`}
                            className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {row.inCatalog === false ? (
                          <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                            Not a link — crawler hit
                          </div>
                        ) : row.label ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {row.label}
                            {isPrintedGoSlug(row.slug) ? ' · printed' : ''}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <a
                          href={row.destination_url}
                          target="_blank"
                          rel="noreferrer"
                          title={row.destination_url}
                          className="block max-w-[22rem] truncate text-xs text-gray-600 dark:text-gray-300 hover:underline"
                        >
                          {row.destination_url}
                        </a>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <ClickBar clicks={row.clicks} max={maxClicks} />
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
                        {formatWhen(row.lastClickedAt)}
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setQrSlug(row.slug)}
                          className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title={`QR for /${row.slug}`}
                          aria-label={`QR for /${row.slug}`}
                        >
                          <QrCode className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(row);
                            setModalOpen(true);
                          }}
                          className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title={`Edit /${row.slug}`}
                          aria-label={`Edit /${row.slug}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/20 p-4">
          <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">
            How this works
          </h2>
          <ol className="list-decimal pl-4 space-y-2 text-sm text-amber-950 dark:text-amber-100/90">
            <li>Add, edit, or delete a row here. That is the live destination.</li>
            <li>The Worker reads Supabase (cached ~15s). No deploy.</li>
            <li>
              Printed slugs: <code>/card</code>, <code>/sticker</code>, <code>/phone</code> — change the URL, never
              the slug.
            </li>
          </ol>
          <p className="mt-3 text-xs text-amber-800 dark:text-amber-200/80">
            Custom domain: Cloudflare → Worker <strong>lccto-go</strong> → Domains → <code>go.stephansmith.me</code>.
          </p>
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Countries</h2>
          </div>
          {countries.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">No clicks yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {countries.map((row) => (
                <li
                  key={row.country}
                  className="px-4 py-2 flex items-center justify-between text-sm text-gray-800 dark:text-gray-200"
                >
                  <span>{row.country}</span>
                  <span className="tabular-nums text-gray-500">{row.clicks}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Recent clicks</h2>
          </div>
          {recent.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
              Scan a QR or open a go link after the Worker is deployed with Supabase secrets.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2">When</th>
                    <th className="px-4 py-2">Slug</th>
                    <th className="px-4 py-2">Country</th>
                    <th className="px-4 py-2">Host</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((click) => (
                    <tr
                      key={click.id}
                      className="border-t border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-200"
                    >
                      <td className="px-4 py-2 whitespace-nowrap text-xs">
                        {formatWhen(click.created_at)}
                      </td>
                      <td className="px-4 py-2 font-medium">/{click.slug}</td>
                      <td className="px-4 py-2 text-xs">{click.country || '—'}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{click.host || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <GoLinkModal
        isOpen={modalOpen}
        link={editing}
        onClose={() => setModalOpen(false)}
        onSuccess={() => void load()}
      />
      {qrSlug ? <GoQrModal slug={qrSlug} onClose={() => setQrSlug(null)} /> : null}
    </div>
  );
}
